import React, {useEffect, useState} from "react";
import {Breadcrumbs} from "../../Widgets/Breadcrumbs/Breadcrumbs";
import {formatDateTime} from "../../../helpers/FormatHelper";
import AsyncIndicator from "../../Widgets/AsyncIndicator/AsyncIndicator";
import {FetchError} from "../../Widgets/FetchError/FetchError";
import NotFound from "../../Widgets/NotFound/NotFound";
import {EcgDetailsTable} from "../../Widgets/EcgDetailsTable/EcgDetailsTable";
import {EcgViewer} from "../../Widgets/EcgViewer/EcgViewer";
import {useAbortController, useAccessInfo, useAppDispatch, useAppSelector} from "../../../hooks";
import {Role} from "../../../models/Role";
import {useParams} from "react-router-dom";
import {selectToken} from "../../../features/account/accountSlice";
import {Record} from "../../../models/Record";
import {EcgData} from "../../../models/EcgData";
import ErrorResponse from "../../../models/ErrorResponse";
import handleErrors from "../../../helpers/ErrorHandler";
import * as ErrorCodes from "../../../api/ErrorCodes";
import {ecgTestData} from "../../../ecg/EcgTest";
import * as ApiHelper from "../../../api/ApiHelper";
import {Conclusion} from "../../Widgets/Conclusion/Conclusion";
import {logError} from "../../../helpers/LogHelper";


export function SharedEcgView() {
    const isSupport = useAccessInfo(Role.Support);
    let {recordToken = ""} = useParams();
    const [controller] = useAbortController();
    const token = useAppSelector(selectToken);
    const dispatch = useAppDispatch();
    const [isFetching, setFetchingState] = useState(true);
    const [hasError, setErrorState] = useState(false);
    const [notFound, setNotFoundState] = useState(false);
    const [record, setRecord] = useState(null as Record | null);
    const [data, setData] = useState(null as EcgData | null);
    const dataHandler = (data: EcgData) => {
        setFetchingState(false);
        setErrorState(false);
        setNotFoundState(false);
        setData(data);
    };
    const recordHandler = (record: Record) => {
        setErrorState(false);
        setNotFoundState(false);
        setRecord(record);
    };
    const errorHandler = (error: ErrorResponse) => {
        logError("Ecg data fetch error", error);
        if (!handleErrors(error, dispatch)) {
            if (error.ResultCode === ErrorCodes.NotFound) {
                setNotFoundState(true);
                setErrorState(false);
            } else {
                setErrorState(true);
                setNotFoundState(false);
            }
            setFetchingState(false);
        }
    };
    const fetchData = (record: Record) => {
        if (process.env.NODE_ENV === 'development') {
            //For testing
            dataHandler(ecgTestData);
        } else {
            //Main code
            let userToken = token?.token;
            if (userToken && record.ecgStudy) {
                setFetchingState(true);
                ApiHelper.getEcgDataWithToken(recordToken, record.ecgStudy.ecgId, controller, dataHandler, errorHandler);
            } else {
                setErrorState(true);
            }
        }
    };
    const fetchRecord = () => {
        let userToken = token?.token;
        if (userToken) {
            setFetchingState(true);
            ApiHelper.getRecordWithToken(recordToken, controller, recordHandler, errorHandler);
        } else {
            setErrorState(true);
        }
    };
    useEffect(() => fetchRecord(), [recordToken]);     // eslint-disable-line
    useEffect(() => {
        if (record) {
            fetchData(record);
        }
    }, [record]);       // eslint-disable-line
    const isOk = !hasError && !notFound && !isFetching;
    return (
        <div>
            <Breadcrumbs
                data={new Map([[record?.id ?? "_", record ? formatDateTime(record.dateTime) : null], [record?.patientId ?? "_", record?.patientName ?? null]])}/>
            {isFetching && <AsyncIndicator/>}
            {!isFetching && hasError && <FetchError onRetry={fetchRecord}/>}
            {!isFetching && notFound && <NotFound/>}
            {(isOk && record && data && (data.ecg[0].length ?? 0) > 0) &&
                <EcgDetailsTable record={record} isSupport={isSupport} data={data}/>
            }
            {(isOk && record && data && (data.ecg[0].length ?? 0) > 0) &&
                <EcgViewer ecgData={data} record={record} recordId={record.id} studyId={record.ecgStudy.ecgId}
                           isSupport={isSupport} recalculationRequest={fetchRecord}/>
            }
            {(isOk && record) && <Conclusion initialValue={record.ecgStudy.ecgConclusion ?? ""}
                                             cachedValue={record.ecgStudy.ecgConclusion ?? ""}/>}
        </div>
    );
}
